import { reportProfileFooterDocument, reportProfileSenderDocument, type ReportRichTextDocument } from "./reportRichText";

export type Identity = {
  user_id: string;
  tenant_id: string;
  tenant: string;
  email: string;
  role: string;
};

export type RemoteDataSaver = "off" | "medium" | "strong" | "maximum";
export type RemoteWebState = {
  data_saver_supported?: boolean;
  data_saver?: RemoteDataSaver;
  video_encoder?: boolean;
  reason?: string;
  state: "inactive" | "connecting" | "active";
  supported: boolean;
  online: boolean;
  owned?: boolean;
  port?: number;
  scheme?: "http" | "https";
  expires_at?: string;
};

export function getRemoteWebAccess(deviceID: string, signal?: AbortSignal): Promise<RemoteWebState> {
  return request<RemoteWebState>(`/api/admin/devices/${deviceID}/remote-web`, { signal });
}

export function startRemoteWebAccess(deviceID: string, input: { port: number; scheme: "http" | "https"; minutes: number; data_saver: RemoteDataSaver }): Promise<RemoteWebState> {
  return request<RemoteWebState>(`/api/admin/devices/${deviceID}/remote-web`, { method: "POST", body: JSON.stringify(input) });
}

export function stopRemoteWebAccess(deviceID: string): Promise<void> {
  return request<void>(`/api/admin/devices/${deviceID}/remote-web`, { method: "DELETE" });
}

export function openRemoteWebAccess(deviceID: string): Promise<{ url: string; ticket: string }> {
  return request<{ url: string; ticket: string }>(`/api/admin/devices/${deviceID}/remote-web/open`, { method: "POST" });
}

export type Capabilities = {
  sandbox_simulation: {
    enabled: boolean;
    measurement_point_id?: string;
    access?: "read" | "manage";
    can_manage: boolean;
  };
  measurement_point_groups?: {
    enabled: boolean;
    profiles: string[];
    can_manage: boolean;
  };
};

// Measurement point groups (plan §4.5.1). Window states, counting basis and
// origin rows are provisional until O3/O12 and O2 are decided.
export type NarrowPassageDirection = "1_to_2" | "2_to_1";
export type NarrowPassageViewName = "timeline" | "wait_distribution" | "origin" | "quality" | "passages";
export type NarrowPassageLineRoles = {
  configuration_generation: number;
  outer_line_id: string;
  inner_line_id: string;
  toward_direction: "A_to_B" | "B_to_A";
  revision?: number;
};

export type MeasurementPointGroupSummary = {
  id: string;
  name: string;
  profile: string;
  timezone: string;
  campaign?: { from_date: string; to_date: string } | null;
  access: "read" | "manage";
  state: "ready" | "line_roles_missing";
  member_count: number;
};

export type MeasurementPointGroupMember = {
  measurement_point_id: string;
  name: string;
  side: 1 | 2;
  configuration_generation: number;
  line_roles: NarrowPassageLineRoles | null;
  lines: { id: string; name: string }[];
};

export type MeasurementPointGroup = Omit<MeasurementPointGroupSummary, "member_count"> & {
  missing_line_roles: string[];
  members: MeasurementPointGroupMember[];
};

// Platform list: members by side, without line roles, state or measurement data.
export type ManagedMeasurementPointGroup = {
  id: string;
  name: string;
  profile: string;
  timezone: string;
  campaign: { from_date: string; to_date: string } | null;
  members: { measurement_point_id: string; name: string; side: number }[];
};

export type ManagedMeasurementPointGroupInput = {
  name: string;
  profile: "narrow_passage";
  timezone: string;
  campaign: { from_date: string; to_date: string } | null;
  members: { measurement_point_id: string; side: 1 | 2 }[];
};

export type NarrowPassageCounts = { total: number } & Record<NarrowPassageDirection, number>;
export type NarrowPassageMatchRate = { paired: number; passages: number; rate: number | null };
export type NarrowPassageMatchRates = { total: NarrowPassageMatchRate } & Record<NarrowPassageDirection, NarrowPassageMatchRate>;
export type NarrowPassageStatus = "paired_plate" | "paired" | "inbound_only" | "outbound_only" | "discarded";
// Data quality g. Counts are trucks like a–d; match_rate_all_classes is the
// only figure over all classes. plates_confirmed_locally is a local OCR
// consensus, never a confirmation by both sides (only paired_plate is).
export type NarrowPassageQuality = {
  statuses: { status: NarrowPassageStatus; counts: NarrowPassageCounts }[];
  discard_reasons: { reason: string; counts: NarrowPassageCounts }[];
  quality_reasons: { reason: string; counts: NarrowPassageCounts }[];
  discarded_unclassified: number;
  match_rate_all_classes: NarrowPassageMatchRates;
  sides: { side: 1 | 2; rows: number; class_samples_median: number | null; class_share_median: number | null; plates_read: number; plate_ocr_confidence_median: number | null; plates_confirmed_locally: number }[];
  clock: { date: string; segment_id?: string; delta_s?: number; pairs_1_to_2: number; pairs_2_to_1: number; warning: boolean }[];
};

// One row of the passage list behind f and g: all classes and discarded rows,
// with permitted origin and safe source details. Private event/hash/key ids are excluded.
export type NarrowPassageListItem = {
  ts: string;
  direction?: NarrowPassageDirection;
  class?: string;
  status: NarrowPassageStatus;
  reasons?: string[];
  outer_ms?: number;
  inbound_ms?: number;
  outbound_ms?: number;
  wait_s?: number;
  transit_s?: number;
  segment_id?: string;
  purpose?: "setup" | "measurement";
  revision?: number;
  owner_window?: string;
  plate_country?: string;
  plate_state?: string;
  plate_district?: string;
  sources?: { side: number; role: string; ts: string; schema_version: number; quality: Record<string, unknown> }[];
};

export type NarrowPassageView = {
  group_id: string;
  state: "ready" | "line_roles_missing" | "no_data";
  completeness?: "unproven";
  current_context?: { side_1_assignment_id: string; side_2_assignment_id: string; roles: { assignment_id: string; configuration_generation: number; outer_line_id: string; inner_line_id: string; toward_direction: string }[] };
  notices?: string[];
  segments?: {
    id: string; start: string; end: string | null; purpose: "setup" | "measurement";
    revision: number; stored_revision?: number; matcher_version: number;
    source_count: number; receive_stand: Record<string, number>;
    computed_at: string | null; error_code?: string; parameters?: NarrowPassageParameters;
    context?: { side_1_assignment_id: string; side_2_assignment_id: string; roles: { assignment_id: string; configuration_generation: number; outer_line_id: string; inner_line_id: string; toward_direction: string }[] };
  }[];
  view: NarrowPassageViewName;
  timezone: string;
  bucket_seconds: number;
  range_start: string;
  range_end: string;
  missing_line_roles?: string[];
  matcher_version?: number;
  passage_count?: number | null;
  windows?: {
    start: string; end: string; window_state: "provisional" | "revised";
    segment_id?: string; purpose?: "setup" | "measurement"; revision?: number;
    completeness?: "unproven"; plate_basis?: string; reasons?: string[];
    computed_at?: string; matcher_version?: number;
  }[];
  excluded?: { discarded: number; time_unreliable: number };
  wait_count?: number | null;
  series?: { direction: NarrowPassageDirection; points: { ts: string; data_state?: "observed" | "unknown"; passages: number | null; wait_count: number; wait_mean_s: number | null; wait_max_s: number | null }[] }[];
  wait_class_seconds?: number;
  wait_classes?: { from_s: number; to_s: number | null; counts: NarrowPassageCounts }[];
  origin?: { not_read: NarrowPassageCounts; origins: { country: string; state: string | null; district: string | null; counts: NarrowPassageCounts }[] };
  match_rate?: NarrowPassageMatchRates;
  quality?: NarrowPassageQuality;
  passages?: NarrowPassageListItem[];
};

export type NarrowPassageParameters = {
  min_transit_ms: number; max_transit_ms: number; max_wait_ms: number;
  median_scale_ms: number; median_cap: number; class_conflict_cost: number;
  same_hash_cost: number; hash_conflict_cost: number; gap_cost: number;
  follow_up_scale_ms: number; follow_up_window_ms: number; follow_up_cap: number;
  hash_evidence: boolean; clock_min_pairs: number; clock_warning_s: number; time_zone: string;
};

export function getManagedNarrowPassageView(tenantID: string, groupID: string, from: Date, to: Date): Promise<NarrowPassageView> {
  const query = new URLSearchParams({ view: "timeline", purpose: "all", from: from.toISOString(), to: to.toISOString(), resolution: "3600" });
  return request(`/api/tenants/${encodeURIComponent(tenantID)}/measurement-point-groups/${encodeURIComponent(groupID)}/narrow-passage?${query}`);
}

export function reviseManagedNarrowPassage(tenantID: string, groupID: string, input: { segment_id: string; revision: number; purpose: "setup" | "measurement"; parameters: NarrowPassageParameters; recompute: boolean; reason: string }): Promise<void> {
  return request(`/api/tenants/${encodeURIComponent(tenantID)}/measurement-point-groups/${encodeURIComponent(groupID)}/narrow-passage/segments`, { method: "POST", body: JSON.stringify(input) });
}

export type ReportProfile = {
  title: string;
  sender_block: string;
  sender_document?: ReportRichTextDocument;
  footer: string;
  footer_document?: ReportRichTextDocument;
  logo_png?: string;
  logo_layout: "compact" | "full_width";
  updated_at?: string;
};

export type Device = {
  id: string;
  device_id?: string;
  assignment_id?: string;
  cn: string;
  serial_number?: string;
  location: string;
  active: boolean;
  broker_endpoint?: string;
  cert_expires_at?: string;
  status?: DeviceStatus;
  agent_update?: AgentUpdate;
  network_usage?: DeviceNetworkUsage;
  heartbeat?: DeviceHeartbeat;
};

export type DeviceHeartbeat = {
  config_version: number;
  interval_s: number;
  offline_after_s: number;
  agent_version?: string;
  supported: boolean;
  audit_id?: number;
};

export type DeviceOperations = {
  agent_version?: string;
  cancel_supported: boolean;
  pending_attempts: number;
  pending_deletions: number;
  pending_agent_update: boolean;
  config_rejection?: {
    requested_version: number;
    applied_version: number;
    error_code: string;
    diagnostic?: string;
    rejected_at: string;
  };
};

export type NetworkUsageConfig = {
  schema: 1;
  enabled: boolean;
  period: "never" | "calendar_month" | "fixed_30_days";
  reset_day?: number;
  timezone: string;
  quota_bytes?: number;
  warning_percent?: number;
  reset_generation: number;
  interfaces?: string[];
};

export type NetworkUsageStatus = {
  state: "disabled" | "active" | "error";
  // "wan" excludes the local network. "interface" means the privileged sampler
  // was unavailable and the figure still contains local traffic, which can be
  // orders of magnitude larger than anything a tariff would bill.
  scope?: "wan" | "interface";
  config_version: number;
  reset_generation: number;
  rx_bytes: number;
  tx_bytes: number;
  period_started_at?: string;
  period_ends_at?: string;
  sampled_at: string;
  interfaces?: string[];
  error_code?: string;
};

export type DeviceNetworkUsage = {
  config_version: number;
  settings: NetworkUsageConfig;
  status?: NetworkUsageStatus;
  synchronized: boolean;
  warning: boolean;
  quota_exceeded: boolean;
  total_bytes: number;
  warning_threshold_bytes?: number;
  audit_id?: number;
};

export type MeasurementPoint = {
  id: string;
  name: string;
  active: boolean;
  assignment?: {
    id: string;
    starts_at: string;
    device: Device;
  };
};

export function deviceLabel(device: Device): string {
  return device.serial_number || device.cn;
}

export type AgentUpdate = {
  desired_version: string;
  desired_sequence: number;
  state: "requested" | "downloading" | "verified" | "installing" | "healthy" | "superseded" | "rolled_back" | "failed";
  requested_at?: string;
  reported_version?: string;
  reported_sequence?: number;
  reported_at?: string;
  error_code?: string;
};

export type DeviceStatus = {
  online: boolean;
  last_seen: string;
  reported_at?: string;
  agent_version?: string;
  discovered_files: number;
  pending_events: number;
  dropped_measurements: number;
  last_scan_at?: string;
  last_event_at?: string;
  last_sync_at?: string;
  last_error?: string;
  tracked_service_name?: string;
  tracked_service_state?: string;
  tracked_service_online?: boolean;
  service_checked_at?: string;
  measurement_source_name?: string;
  measurement_source_state?: string;
  measurement_source_online?: boolean;
  measurement_source_checked_at?: string;
  local_address?: string;
  storage?: StorageStatus;
};

export type TrafficCount = {
  measurement_point_id: string;
  ts: string;
  count: number;
  class: string;
};

export type TrafficCountResult = {
  counts: TrafficCount[];
  bucket_seconds: number;
  source_rows: number;
  total_count: number;
  class_count: number;
};

export type FlowEvent = {
  measurement_point_id: string;
  event_id: string;
  ts: string;
  class: string;
  flow: string;
  path_id: number;
  flow_uid: string;
  points: number;
  duration_s: number | null;
  sensor: string;
  plate_text_hash: string;
  plate_candidates_hash: string;
};

export type DatasetField = {
  name: string;
  kind: "measure" | "dimension" | "attribute";
  type: "integer" | "number" | "boolean" | "enum" | "text" | "timestamp";
  title: string;
  unit?: string;
  values?: string[];
  aggregations?: ("sum" | "count" | "min" | "max" | "avg")[];
  runtime_catalog?: string;
};

export type DefaultPanel = {
  type: MeasurementPanel["type"];
  dataset: string;
  measure?: string;
  agg?: MeasurementPanel["aggregation"];
  group_by?: string[];
  grain?: "auto" | "1m" | "15m" | "1h" | "1d";
  title: string;
  expr?: string;
  format?: string;
  show_zoom_slider?: boolean;
  repeat_by?: string;
};

export type Dataset = {
	traffic_routes?: boolean;
  id: number;
  extension_id: string;
  dataset: string;
  title: string;
  schema_version: number;
  origin?: "extension" | "simulation";
  fields: DatasetField[];
  default_panels: DefaultPanel[];
  runtime_values?: Record<string, { value: string; label: string }[]>;
};

export type MeasurementPanel = {
  id?: string;
  type: "kpi" | "timeseries" | "table" | "histogram" | "status" | "map" | "time_profile";
  dataset_id: number;
  title: string;
  measure?: string;
  aggregation?: "sum" | "count" | "min" | "max" | "avg";
  group_by: string[];
  filters?: Record<string, string>;
  grain_seconds?: 60 | 900 | 3600 | 86400;
  expression?: "sum" | "count" | "avg" | "min" | "max" | "rate_per_hour" | "percent" | "";
  format?: string;
  show_zoom_slider?: boolean;
  map_scope?: "measurement_point" | "tenant";
  time_profile?: TimeProfileConfiguration;
  position: number;
  preparation_state?: "backfilling" | "active" | "grace" | "failed" | "";
};

export type TimeProfileWeekdayGroup = {
  name: string;
  weekdays: number[];
};

export type TimeProfileTimeBlock = {
  name: string;
  start: string;
  end: string;
  ends_next_day?: boolean;
};

export type TimeProfileConfiguration = {
  allow_overlaps: boolean;
  weekday_groups: TimeProfileWeekdayGroup[];
  time_blocks: TimeProfileTimeBlock[];
  metric: "average_per_calendar_day" | "total";
};

export type TimeProfileCell = {
  weekday_group_index: number;
  value: number;
  day_count: number;
};

export type TimeProfileRow = {
  class_value: string;
  time_block_index?: number;
  values: TimeProfileCell[];
};

export type TimeProfileData = {
  metric: TimeProfileConfiguration["metric"];
  weekday_groups: TimeProfileWeekdayGroup[];
  time_blocks: TimeProfileTimeBlock[];
  rows: TimeProfileRow[];
};

export type PanelDataPoint = {
  ts: string;
  groups: Record<string, unknown>;
  value: number;
  sum?: number;
  count?: number;
};

export type MapLineData = {
  id: string;
  name: string;
  geometry: {
    type: "LineString";
    coordinates: [number, number][];
  };
};

export type MapMovementData = {
  entry_line_id: string;
  exit_line_id: string;
  mode: "auto" | "manual" | "disabled";
  waypoints: [number, number][];
};

export type MapSchematicPoint = [number, number];

export type MapSchematicRoadArm = {
  id: string;
  osm_way_id: number;
  osm_way_ids?: number[];
  name: string;
  highway: string;
  one_way: boolean;
  lane_count: number | null;
  width_m: number;
  width_source: "mapped" | "derived" | "manual";
  bearing_degrees: number;
  centerline: MapSchematicPoint[];
  line_id: string | null;
};

export type MapSchematicPath = {
  entry_line_id: string;
  exit_line_id: string;
  mode: "auto" | "manual" | "disabled";
  waypoints: MapSchematicPoint[];
};

export type MapSchematicGeometry = {
  schema: 1;
  source: {
    type: "openstreetmap";
    imported_at: string;
    endpoint: string;
    query_radius_m: number;
    requested_center: [number, number];
    junction: [number, number];
    junction_node_id: number;
    junction_node_ids?: number[];
    copyright: string;
  };
  view_box: { width: 1000; height: 1000 };
  arms: MapSchematicRoadArm[];
  paths: MapSchematicPath[];
};

export type MapMockTraffic = {
  from_date: string;
  to_date: string;
  intensity: "quiet" | "normal" | "busy";
  seed: string;
  configuration_generation: number;
  bucket_count: number;
  generated_at: string;
};

export type MockTrafficOverview = {
  measurement_point_id: string;
  measurement_point: string;
  configuration_generation: number;
  mock_traffic: MapMockTraffic | null;
  mock_traffic_stale: boolean;
};

export type MapLineCatalog = {
  measurement_point_id: string;
  dataset: string;
  latitude: number;
  longitude: number;
  configuration_generation: number;
  synchronized_generation: number | null;
  synchronization_state: "not_synchronized" | "changed" | "synchronized";
  revision_count: number;
  updated_at: string;
  lines: MapLineData[];
  movements: MapMovementData[];
  schematic: MapSchematicGeometry | null;
  mock_traffic: MapMockTraffic | null;
  mock_traffic_stale: boolean;
};

export type MapMeasurementPointData = {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  configuration_generation?: number;
  lines?: MapLineData[];
  movements?: MapMovementData[];
  schematic?: MapSchematicGeometry | null;
  points: PanelDataPoint[];
};

export type PanelData = {
	traffic?: TrafficConfiguration[];
  panel_id: string;
  state: string;
  timezone?: string;
  bucket_seconds?: number;
  range_start?: string;
  range_end?: string;
  points: PanelDataPoint[];
  measurement_points?: MapMeasurementPointData[];
  time_profile?: TimeProfileData;
  time_unreliable_count?: number;
};

export type TrafficDuration = { from_index: number; to_index: number; sum_s: number; samples: number };
export type TrafficRoute = { class: string; route: { line_id: string; direction: string }[]; vehicles: number; durations: TrafficDuration[]; total?: TrafficDuration };
export type TrafficLayout = { revision: number; latitude: number | null; longitude: number | null; lines: MapLineData[]; schematic: MapSchematicGeometry | null };
export type TrafficConfiguration = { assignment_id: string; generation: number; first_seen: string; routes: TrafficRoute[]; layout: TrafficLayout };
export type TrafficLayoutInput = { assignment_id: string; generation: number; revision: number; latitude: number; longitude: number; schematic: MapSchematicGeometry | null; radius_m?: number };

export async function updateTrafficLayout(pointID: string, datasetID: number, input: TrafficLayoutInput, importOSM = false): Promise<TrafficLayout> {
  const response = await request<{ layout: TrafficLayout }>(`/api/measurement-points/${pointID}/datasets/${datasetID}/traffic-layout${importOSM ? "/import" : ""}`, { method: importOSM ? "POST" : "PUT", body: JSON.stringify(input) });
  return response.layout;
}

export type ManagedTenant = {
  id: string;
  name: string;
  created_at: string;
  admin_email: string;
  active_device_count: number;
  total_device_count: number;
  archived_at?: string | null;
};

export type GlobalDevice = {
  cleanup_pending?: boolean;
  device: Device;
  installation?: DeviceInstallation;
  assignment_id?: string;
  measurement_point_id?: string;
  measurement_point?: string;
  tenant_id?: string;
  tenant?: string;
};

export type UnassignedMeasurementGroup = {
  device_id: string;
  device_cn: string;
  serial_number: string;
  claimed_assignment_id: string;
  first_event_at: string;
  last_event_at: string;
  record_count: number;
  vehicle_count: number;
  first_received_at: string;
  last_received_at: string;
};

export type DeviceEnrollment = {
  install_command: string;
  expires_at: string;
  installation_id: string;
};

export type DeviceInstallation = {
  id: string;
  device_id: string;
  state: "prepared" | "started" | "running" | "succeeded" | "failed";
  current_step: string;
  error_code?: string;
  error_summary?: string;
  exit_code?: number;
  architecture?: string;
  os_id?: string;
  os_version?: string;
  agent_version?: string;
  created_at: string;
  started_at?: string;
  finished_at?: string;
  report_expires_at: string;
  event_count: number;
};

export type AgentRelease = {
  version: string;
  sequence?: number;
  remote_update_from?: string;
};

export type DeviceExtension = {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  reported_enabled?: boolean;
  one_way: boolean;
  latest_version: string;
  desired_version?: string;
  installed_version?: string;
  state?: string;
  status_message?: string;
  error_code?: string;
  update_available: boolean;
  minimum_agent_version: string;
};

export type DeviceExtensions = {
  device_cn: string;
  config_version: number;
  extensions: DeviceExtension[];
  changed?: boolean;
  audit_id?: number;
};

export type ExtensionHealth = { online: boolean; reported_at?: string; extensions: Array<{ id: string; name: string; state: string; level?: string; enabled: boolean; installed_version?: string; reported_at: string }> };
export function getExtensionHealth(id: string, global: boolean): Promise<ExtensionHealth> { return request(global ? `/api/admin/devices/${id}/extension-status` : `/api/measurement-points/${id}/extension-status`); }

export type ExtensionPublisher = {
  id: string;
  name: string;
  active: boolean;
  created_at: string;
  submission_count: number;
  published_count: number;
};

export type ExtensionSubmission = {
  id: string;
  publisher_id: string;
  publisher_name: string;
  publisher_active: boolean;
  extension_id: string;
  name: string;
  description: string;
  version: string;
  minimum_agent_version: string;
  artifact_sha256: string;
  artifact_size: number;
  release_size?: number;
  artifact_kind: "executable" | "bundle" | "files";
  runtime: "exec" | "python3";
  restart_policy?: "never" | "on-failure";
  entrypoint: string;
  arguments: string[];
  automatic_checks: ExtensionAutomaticCheck[];
  publisher_key_sha256: string;
  previous_version?: string;
  state: "pending" | "approved" | "published" | "rejected";
  submitted_at: string;
  reviewed_at?: string;
  source_reference?: string;
  license_identifier?: string;
  sbom_sha256?: string;
  review_notes?: string;
  release_sequence?: number;
  published_at?: string;
};

export type ExtensionReviewDecision = {
  review_notes: string;
  release_approved: boolean;
};

export type ExtensionAutomaticCheck = {
  code: string;
  label: string;
  detail: string;
  passed: boolean;
};

export type ExtensionDevelopmentDevice = {
  device_id: string;
  device_cn: string;
  serial_number: string;
  selected_at: string;
  audit_id?: number;
};

export type ExtensionDevelopmentSandbox = {
  initialized: boolean;
  login_identifier: string;
  measurement_point_name: string;
  generation: number;
  measurement_point_id?: string;
};

export type ExtensionDevelopmentAccess = {
  publisher_id: string;
  publisher_name: string;
  publisher_active: boolean;
  device: ExtensionDevelopmentDevice | null;
  sandbox: ExtensionDevelopmentSandbox;
};

export type ExtensionDevelopmentSandboxCredentials = {
  login_identifier: string;
  password: string;
};

export type ExtensionDevelopmentDeviceCandidate = {
  device_id: string;
  device_cn: string;
  serial_number: string;
  agent_version: string;
  eligible: boolean;
  blockers: ("inactive" | "agent_too_old" | "customer_assignment" | "customer_measurements" | "cleanup_pending")[];
  assigned_publisher_id?: string;
  assigned_publisher_name?: string;
};

export type ExtensionDevelopmentAttempt = {
  attempt_id: string;
  revision_id: string;
  extension_id: string;
  name: string;
  revision_number: number;
  publisher_name: string;
  device_id: string;
  device_cn: string;
  serial_number: string;
  action: "build" | "release" | "cancel";
  state: "uploading" | "queued" | "downloading" | "preparing" | "building" | "installing" | "running" | "succeeded" | "failed" | "release_uploading" | "release_ready" | "cancelling";
  step: string;
  error_code?: string;
  error_summary?: string;
  analysis?: Record<string, unknown>;
  release_version?: string;
  minimum_agent_version?: string;
  restart_policy?: "never" | "on-failure";
  submission_id?: string;
  created_at: string;
  startup_verified_at?: string;
  finished_at?: string;
  can_delete: boolean;
  delete_blocker?: "active" | "current" | "release_bound";
  source_stats_known?: boolean;
  source_total_files?: number;
  source_total_bytes?: number;
  source_reused_files?: number;
  source_reused_bytes?: number;
  source_uploaded_files?: number;
  source_uploaded_bytes?: number;
};

export type ExtensionDevelopmentOutputChunk = {
  cursor: number;
  run_id: string;
  sequence: number;
  stream: "stdout" | "stderr";
  emitted_at: string;
  received_at: string;
  data_base64: string;
};

export type ExtensionDevelopmentOutput = {
  attempt_id: string;
  extension_id: string;
  state: ExtensionDevelopmentAttempt["state"];
  chunks: ExtensionDevelopmentOutputChunk[];
  next_cursor: number;
  truncated: boolean;
};

export function supportsRemoteUpdates(device: Device): boolean {
  const match = /^(\d+)\.(\d+)\.(\d+)$/.exec(device.status?.agent_version ?? "");
  if (!match) return false;
  const [major, minor] = [Number(match[1]), Number(match[2])];
  return major > 0 || minor >= 3;
}

export function supportsExtensions(device: Device): boolean {
  const match = /^(\d+)\.(\d+)\.(\d+)$/.exec(device.status?.agent_version ?? "");
  if (!match) return false;
  const [major, minor] = [Number(match[1]), Number(match[2])];
  return major > 0 || minor >= 5;
}

export function supportsRemoteRestart(device: Device): boolean {
  const match = /^(\d+)\.(\d+)\.(\d+)$/.exec(device.status?.agent_version ?? "");
  if (!match) return false;
  const [major, minor] = [Number(match[1]), Number(match[2])];
  return major > 0 || minor >= 9;
}

function compareAgentVersions(left: string, right: string): number | null {
  const leftMatch = /^(\d+)\.(\d+)\.(\d+)$/.exec(left);
  const rightMatch = /^(\d+)\.(\d+)\.(\d+)$/.exec(right);
  if (!leftMatch || !rightMatch) return null;
  for (let index = 1; index <= 3; index += 1) {
    const difference = Number(leftMatch[index]) - Number(rightMatch[index]);
    if (difference !== 0) return difference;
  }
  return 0;
}

export function isAgentReleaseNewer(device: Device, release: AgentRelease): boolean {
  const installed = device.status?.agent_version;
  if (!installed) return true;
  const comparison = compareAgentVersions(release.version, installed);
  return comparison === null || comparison > 0;
}

export function isSupersededAgentUpdate(device: Device): boolean {
  const installed = device.status?.agent_version;
  const desired = device.agent_update?.desired_version;
  if (!installed || !desired) return false;
  const comparison = compareAgentVersions(installed, desired);
  return comparison !== null && comparison > 0;
}

export function isAgentUpdateConfirmed(device: Device): boolean {
  const update = device.agent_update;
  if (!update || update.state !== "healthy" || device.status?.agent_version !== update.desired_version ||
    update.reported_version !== update.desired_version || update.reported_sequence !== update.desired_sequence) return false;
  const requested = Date.parse(update.requested_at ?? "");
  const heartbeat = Date.parse(device.status.reported_at ?? device.status.last_seen);
  return Number.isFinite(requested) && Number.isFinite(heartbeat) && heartbeat >= requested;
}

export function updateRequiresMaintenance(device: Device, release?: AgentRelease | null): boolean {
  const installedVersion = device.status?.agent_version;
  if (installedVersion && release?.remote_update_from) {
    const floorComparison = compareAgentVersions(installedVersion, release.remote_update_from);
    if (floorComparison !== null && floorComparison < 0) return true;
  }
  const update = device.agent_update;
  if (!update) return false;
  if (update.error_code === "maintenance_required") return true;
  if (update.error_code !== "manifest_invalid") return false;
  const installed = /^(\d+)\.(\d+)\.(\d+)$/.exec(device.status?.agent_version ?? "");
  const desired = /^(\d+)\.(\d+)\.(\d+)$/.exec(update.desired_version);
  if (!installed || !desired) return false;
  const installedBefore08 = Number(installed[1]) === 0 && Number(installed[2]) < 8;
  const desiredAtLeast08 = Number(desired[1]) > 0 || Number(desired[2]) >= 8;
  return installedBefore08 && desiredAtLeast08;
}

export function agentUpdateView(device: Device, release: AgentRelease | null): {
  label: string | null;
  pending: boolean;
  retryable: boolean;
} {
  if (!release || device.status?.agent_version === release.version) {
    return { label: null, pending: false, retryable: false };
  }
  if (!isAgentReleaseNewer(device, release)) {
    return { label: `Installierter Agent ${device.status?.agent_version} ist neuer als Server-Release ${release.version}`, pending: false, retryable: false };
  }
  if (updateRequiresMaintenance(device, release)) {
    return { label: "Wartungsinstallation erforderlich", pending: false, retryable: false };
  }
  const update = device.agent_update;
  if (!update) {
    return { label: `Verfügbar: ${release.version}`, pending: false, retryable: false };
  }
  if (update.desired_version !== release.version) {
    const active = ["requested", "downloading", "verified", "installing", "healthy"].includes(update.state) && !isAgentUpdateConfirmed(device) && !isSupersededAgentUpdate(device);
    const previous = active
      ? `Update ${update.desired_version} angefordert`
      : update.state === "failed" || update.state === "rolled_back"
        ? `Update ${update.desired_version} fehlgeschlagen`
        : `Installiert: ${update.desired_version}`;
    return { label: `${previous} · ${release.version} verfügbar`, pending: active, retryable: false };
  }
  switch (update.state) {
    case "requested":
      return { label: `Update ${release.version} angefordert`, pending: true, retryable: false };
    case "downloading":
      return { label: `Update ${release.version} wird geladen`, pending: true, retryable: false };
    case "verified":
      return { label: "Release geprüft · Aktivierung ausstehend", pending: true, retryable: false };
    case "installing":
      return { label: `Update ${release.version} wird installiert`, pending: true, retryable: false };
    case "healthy":
      return { label: "Update abgeschlossen · neuer Heartbeat ausstehend", pending: true, retryable: false };
    case "superseded":
      return { label: "Früherer Updateauftrag durch neuere Installation ersetzt", pending: false, retryable: false };
    case "rolled_back":
      return { label: "Update wurde zurückgerollt", pending: false, retryable: true };
    case "failed":
      return updateRequiresMaintenance(device, release)
        ? { label: "Wartungsinstallation erforderlich", pending: false, retryable: false }
        : { label: "Update fehlgeschlagen", pending: false, retryable: true };
  }
}

export class APIError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code = "",
  ) {
    super(message);
  }
}

export type NotificationPreferences = { offline: boolean; storage: boolean; program: boolean; database: boolean; critical: boolean };
export type DeviceNotifications = { assigned: boolean; customer: string | null; measurement_point: string | null; settings: NotificationPreferences };
export function getDeviceNotifications(id: string): Promise<DeviceNotifications> { return request(`/api/admin/devices/${id}/notifications`); }
export function setDeviceNotifications(id: string, settings: NotificationPreferences): Promise<DeviceNotifications> { return request(`/api/admin/devices/${id}/notifications`, { method: "PUT", body: JSON.stringify(settings) }); }
export type MailConfig = { enabled: boolean; host: string; port: number; security: "starttls" | "tls"; username: string; password: string; sender: string; recipients: string[] };
export type MailStatus = { config: (Omit<MailConfig, "password"> & { password_saved: boolean }) | null; last_error?: string; last_sent?: number; last_check?: number; test?: string; active_incidents: number };
export type BackupConfig = {
  protocol: string; host: string; port: number; user: string; path: string; url: string;
  domain: string; region: string; access_key: string; known_hosts: string;
  password: string; repository_password: string; secret_key: string; private_key: string; key_password: string;
  allow_insecure_ftp: boolean; key_saved: boolean;
  schedule: Record<"server" | "portal", number>; keep_last: Record<"server" | "portal", number>;
};
export type BackupJob = {
  id: string; action: string; scope: "server" | "portal"; state: string; started: number; error?: string; removed_snapshots?: string[]; deleted_snapshot?: string;
  preview?: { token: string; scope: "server" | "portal"; snapshot: string; files: number; bytes: number; expires: number; confirmation: string; manifest: { revision: string; schema: string } };
};
export type DiskStorage = { total_bytes?: number; free_bytes?: number; used_bytes?: number; sampled_at?: number; state?: string };
export type StorageStatus = { schema: number; sampled_at: string; root: DiskStorage; data: DiskStorage; database_bytes: number; buffer_bytes: number; diagnostic_bytes: number; cleanup?: { generation: number; state: string; removed_events: number; error_code?: string } };
export type ServerStorage = { vps: DiskStorage; backup_storage: DiskStorage; database_bytes?: number | null };
export function cleanupDeviceStorage(id: string, action: string, target_bytes: number, confirmation: string): Promise<{ generation: number }> { return request(`/api/admin/devices/${id}/storage/cleanup`, { method: "POST", body: JSON.stringify({ action, target_bytes, confirmation }) }); }
export type BackupStatus = {
  storage?: ServerStorage;
  config: Omit<BackupConfig, "password" | "repository_password" | "secret_key" | "private_key" | "key_password" | "key_saved"> | null;
  jobs: BackupJob[];
  snapshots: Record<"server" | "portal", Array<{ id: string; time: string; size_bytes?: number }>>;
};
export function backupRequest<T>(path = "", method = "GET", body?: unknown): Promise<T> {
  return request<T>("/api/admin/backups" + path, { method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
}

export type DeliverySettings = { schema: number; send_interval_s: number; confirm_interval_s: number; buffer_mib: number };
export type RawExportJob = { id: string; state: "running" | "ready" | "failed"; error?: string; expires_at: string; size_bytes?: number };
export function getCurrentRawExport(): Promise<{ job: RawExportJob | null }> { return request("/api/raw-exports"); }
export function startRawExport(query: { measurement_point_id?: string; dataset?: string; from?: string; to?: string }): Promise<RawExportJob> { return request("/api/raw-exports", { method: "POST", body: JSON.stringify(query) }); }
export function getRawExport(id: string): Promise<RawExportJob> { return request(`/api/raw-exports/${id}`); }
export function removeRawExport(id: string): Promise<void> { return request(`/api/raw-exports/${id}`, { method: "DELETE" }); }
export async function downloadRawExport(id: string) {
  const response = await fetch(`/api/raw-exports/${id}/csv`, { credentials: "same-origin", headers: portalContextHeaders() });
  if (!response.ok) throw new Error("CSV konnte nicht heruntergeladen werden.");
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a"); link.href = url; link.download = "messdaten.csv"; link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
}
export function getDeliverySettings(id: string): Promise<DeliverySettings> { return request(`/api/admin/devices/${id}/delivery`); }
export function saveDeliverySettings(id: string, settings: DeliverySettings): Promise<DeliverySettings> { return request(`/api/admin/devices/${id}/delivery`, { method: "PUT", body: JSON.stringify(settings) }); }

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...portalContextHeaders(),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    let message = "Die Anfrage konnte nicht abgeschlossen werden.";
    let code = "";
    try {
      const payload = await response.json() as { error?: unknown; code?: unknown };
      if (typeof payload.error === "string" && payload.error.trim()) message = payload.error;
      if (typeof payload.code === "string") code = payload.code;
    } catch {
      // Some infrastructure errors intentionally have no JSON response body.
    }
    throw new APIError(message, response.status, code);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export function getSandboxPublisherID(): string {
  const publisherID = new URLSearchParams(window.location.search).get("sandbox")?.trim() ?? "";
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(publisherID)
    ? publisherID.toLowerCase()
    : "";
}

export function getCustomerTenantID(): string {
  const value = new URLSearchParams(window.location.search).get("customer")?.trim() ?? "";
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value) ? value.toLowerCase() : "";
}
function portalContextHeaders(): Record<string, string> {
  const customer = getCustomerTenantID();
  const sandbox = getSandboxPublisherID();
  return { ...(customer ? { "X-RPControl-Customer": customer } : {}), ...(sandbox ? { "X-RPControl-Sandbox": sandbox } : {}) };
}

export function getSession(): Promise<Identity> {
  return request<Identity>("/api/auth/session");
}

export function getCapabilities(): Promise<Capabilities> {
  return request<Capabilities>("/api/capabilities");
}

export function login(identifier: string, password: string): Promise<Identity> {
  return request<Identity>("/api/auth/login", {
    method: "POST",
    // Keep the established wire field for existing clients; the backend treats
    // its value as an email address or customer/platform login identifier.
    body: JSON.stringify({ email: identifier, password }),
  });
}

export function logout(): Promise<void> {
  return request<void>("/api/auth/logout", { method: "POST" });
}

export async function getDevices(): Promise<Device[]> {
  const response = await request<{ measurement_points: MeasurementPoint[] }>("/api/measurement-points");
  return response.measurement_points.map((point) => ({
    id: point.id,
    device_id: point.assignment?.device.id,
    assignment_id: point.assignment?.id,
    cn: point.assignment?.device.cn ?? "",
    serial_number: point.assignment?.device.serial_number,
    location: point.name,
    active: point.active,
    broker_endpoint: point.assignment?.device.broker_endpoint,
    cert_expires_at: point.assignment?.device.cert_expires_at,
    status: point.assignment?.device.status,
    agent_update: point.assignment?.device.agent_update,
  }));
}

export async function getDatasets(measurementPointID: string): Promise<Dataset[]> {
  const response = await request<{ datasets: Dataset[] }>(`/api/datasets?measurement_point_id=${encodeURIComponent(measurementPointID)}`);
  return response.datasets;
}

export async function getMeasurementPanels(measurementPointID: string): Promise<MeasurementPanel[]> {
  const response = await request<{ panels: MeasurementPanel[] }>(`/api/measurement-points/${measurementPointID}/panels`);
  return response.panels;
}

export async function replaceMeasurementPanels(measurementPointID: string, panels: MeasurementPanel[]): Promise<MeasurementPanel[]> {
  const response = await request<{ panels: MeasurementPanel[] }>(`/api/measurement-points/${measurementPointID}/panels`, {
    method: "PUT",
    body: JSON.stringify({ panels }),
  });
  return response.panels;
}

export async function getMapLineCatalog(measurementPointID: string): Promise<MapLineCatalog> {
  const response = await request<{ catalog: MapLineCatalog }>(`/api/measurement-points/${measurementPointID}/map-lines`);
  return response.catalog;
}

export async function replaceMapLineCatalog(measurementPointID: string, configurationGeneration: number, lines: MapLineData[], movements: MapMovementData[], schematic: MapSchematicGeometry | null, latitude?: number, longitude?: number): Promise<MapLineCatalog> {
  const response = await request<{ catalog: MapLineCatalog }>(`/api/measurement-points/${measurementPointID}/map-lines`, {
    method: "PUT",
    body: JSON.stringify({ configuration_generation: configurationGeneration, lines, movements, schematic, latitude, longitude }),
  });
  return response.catalog;
}

export async function importMapSchematic(measurementPointID: string, configurationGeneration: number, radiusMetres: number): Promise<MapSchematicGeometry> {
  const response = await request<{ schematic: MapSchematicGeometry }>(`/api/measurement-points/${measurementPointID}/map-schematic/import`, {
    method: "POST",
    body: JSON.stringify({ configuration_generation: configurationGeneration, radius_m: radiusMetres }),
  });
  return response.schematic;
}

export async function getMockTrafficOverview(): Promise<MockTrafficOverview[]> {
  const response = await request<{ mock_traffic: MockTrafficOverview[] }>("/api/mock-traffic");
  return response.mock_traffic;
}

export async function generateMapMockTraffic(measurementPointID: string, configurationGeneration: number, fromDate: string, toDate: string, intensity: MapMockTraffic["intensity"], seed: string): Promise<MapMockTraffic> {
  const response = await request<{ mock_traffic: MapMockTraffic }>(`/api/measurement-points/${measurementPointID}/mock-traffic`, {
    method: "POST",
    body: JSON.stringify({ configuration_generation: configurationGeneration, from_date: fromDate, to_date: toDate, intensity, seed }),
  });
  return response.mock_traffic;
}

export async function resetMapMockTraffic(): Promise<number> {
  const response = await request<{ reset_measurement_point_count: number }>("/api/mock-traffic", { method: "DELETE" });
  return response.reset_measurement_point_count;
}

export async function getMeasurementPointGroups(): Promise<MeasurementPointGroupSummary[]> {
  const response = await request<{ measurement_point_groups: MeasurementPointGroupSummary[] }>("/api/measurement-point-groups");
  return response.measurement_point_groups;
}

export async function getMeasurementPointGroup(groupID: string): Promise<MeasurementPointGroup> {
  const response = await request<{ group: MeasurementPointGroup }>(`/api/measurement-point-groups/${encodeURIComponent(groupID)}`);
  return response.group;
}

// All three role fields null removes the member's line roles. Without
// revision the server only creates roles where none exist yet.
export async function replaceNarrowPassageLineRoles(groupID: string, measurementPointID: string, roles: { configuration_generation: number; outer_line_id: string | null; inner_line_id: string | null; toward_direction: NarrowPassageLineRoles["toward_direction"] | null; revision?: number }): Promise<MeasurementPointGroup> {
  const response = await request<{ group: MeasurementPointGroup }>(`/api/measurement-point-groups/${encodeURIComponent(groupID)}/members/${encodeURIComponent(measurementPointID)}/line-roles`, { method: "PUT", body: JSON.stringify(roles) });
  return response.group;
}

export function getNarrowPassageView(groupID: string, view: NarrowPassageViewName, from: Date, to: Date, resolution: "auto" | 900 | 3600 | 86400 = "auto", purpose: "all"|"setup"|"measurement"="all"): Promise<NarrowPassageView> {
  const query = new URLSearchParams({ view, purpose, from: from.toISOString(), to: to.toISOString(), resolution: String(resolution) });
  return request(`/api/measurement-point-groups/${encodeURIComponent(groupID)}/narrow-passage?${query}`);
}

// Local mock API only; the backend has no such route.
export function getMockNarrowPassageScenario(): Promise<{ scenario: string; scenarios: string[] }> { return request("/api/mock-narrow-passage"); }
export function setMockNarrowPassageScenario(scenario: string): Promise<{ scenario: string; scenarios: string[] }> { return request("/api/mock-narrow-passage", { method: "PUT", body: JSON.stringify({ scenario }) }); }

export function getPanelData(panelID: string, from: Date, to: Date, resolution: "auto" | 60 | 900 | 3600 | 86400 = "auto"): Promise<PanelData> {
  return request(`/api/panels/${panelID}/data?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}&resolution=${encodeURIComponent(String(resolution))}`);
}

export async function getReportProfile(): Promise<ReportProfile> {
  const response = await request<{ profile: ReportProfile }>("/api/report-profile");
  return { ...response.profile, logo_layout: response.profile.logo_layout || "compact", sender_document: reportProfileSenderDocument(response.profile), footer_document: reportProfileFooterDocument(response.profile) };
}

export async function saveReportProfile(profile: ReportProfile): Promise<ReportProfile> {
  const response = await request<{ profile: ReportProfile; audit_id: number }>("/api/report-profile", {
    method: "PUT",
    body: JSON.stringify(profile),
  });
  return { ...response.profile, logo_layout: response.profile.logo_layout || "compact", sender_document: reportProfileSenderDocument(response.profile), footer_document: reportProfileFooterDocument(response.profile) };
}

export function getAgentRelease(): Promise<AgentRelease> {
  return request<AgentRelease>("/api/agent-release");
}

export function requestMeasurementPointDeviceUpdate(measurementPointID: string): Promise<{ device_id: string; version: string }> {
  return request(`/api/measurement-points/${measurementPointID}/device-update`, { method: "POST" });
}

export async function getCounts(deviceID: string, from: Date, to: Date, resolution?: "minute"): Promise<TrafficCountResult> {
  const query = new URLSearchParams({
    measurement_point_id: deviceID,
    from: from.toISOString(),
    to: to.toISOString(),
  });
  if (resolution) query.set("resolution", resolution);
  return request<TrafficCountResult>(`/api/measurements?${query}`);
}

export async function getFlowEvents(deviceID: string, from: Date, to: Date): Promise<FlowEvent[]> {
  const query = new URLSearchParams({
    measurement_point_id: deviceID,
    from: from.toISOString(),
    to: to.toISOString(),
    limit: "5000",
  });
  const response = await request<{ flows: FlowEvent[]; limit: number }>(`/api/flows?${query}`);
  return response.flows;
}

export async function getDeviceInstallation(installationID: string): Promise<DeviceInstallation> {
  const response = await request<{ installation: DeviceInstallation }>(`/api/installations/${installationID}`);
  return response.installation;
}

export async function getManagedTenants(): Promise<ManagedTenant[]> {
  const response = await request<{ tenants: ManagedTenant[] }>("/api/admin/tenants");
  return response.tenants;
}

export async function createManagedTenant(input: {
  name: string;
  admin_email: string;
  admin_password: string;
}): Promise<ManagedTenant> {
  const response = await request<{ tenant: ManagedTenant; audit_id: number }>("/api/admin/tenants", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return response.tenant;
}

export function archiveManagedTenant(tenantID: string): Promise<{ tenant_id: string; audit_id: number }> {
  return request(`/api/admin/tenants/${tenantID}`, { method: "DELETE" });
}

export function restoreManagedTenant(tenantID: string): Promise<{ tenant_id: string; audit_id: number }> {
  return request(`/api/admin/tenants/${tenantID}/restore`, { method: "POST" });
}

export function purgeManagedTenant(tenantID: string, confirmation: string): Promise<{ tenant_id: string; audit_id: number }> {
  return request(`/api/admin/tenants/${tenantID}/permanent`, {
    method: "DELETE",
    body: JSON.stringify({ confirmation }),
  });
}

export async function getManagedMeasurementPoints(tenantID: string): Promise<MeasurementPoint[]> {
  const response = await request<{ measurement_points: MeasurementPoint[] }>(`/api/admin/tenants/${tenantID}/measurement-points`);
  return response.measurement_points;
}

export async function getManagedMeasurementPointGroups(tenantID: string): Promise<ManagedMeasurementPointGroup[]> {
  const response = await request<{ measurement_point_groups: ManagedMeasurementPointGroup[] }>(`/api/admin/tenants/${tenantID}/measurement-point-groups`);
  return response.measurement_point_groups;
}

export async function createManagedMeasurementPointGroup(tenantID: string, group: ManagedMeasurementPointGroupInput): Promise<string> {
  const response = await request<{ measurement_point_group: { id: string }; audit_id: number }>(`/api/admin/tenants/${tenantID}/measurement-point-groups`, {
    method: "POST",
    body: JSON.stringify(group),
  });
  return response.measurement_point_group.id;
}

export function deleteManagedMeasurementPointGroup(tenantID: string, groupID: string): Promise<{ audit_id: number }> {
  return request(`/api/admin/tenants/${tenantID}/measurement-point-groups/${encodeURIComponent(groupID)}`, { method: "DELETE" });
}

export async function createManagedMeasurementPoint(tenantID: string, name: string): Promise<MeasurementPoint> {
  const response = await request<{ measurement_point: MeasurementPoint; audit_id: number }>(`/api/admin/tenants/${tenantID}/measurement-points`, {
    method: "POST",
    body: JSON.stringify({ name }),
  });
  return response.measurement_point;
}

export async function getGlobalDevices(): Promise<GlobalDevice[]> {
  const response = await request<{ devices: GlobalDevice[] }>("/api/admin/devices");
  return response.devices;
}

export async function getUnassignedMeasurements(): Promise<UnassignedMeasurementGroup[]> {
  const response = await request<{ groups: UnassignedMeasurementGroup[] }>("/api/admin/unassigned-measurements");
  return response.groups;
}

export function resolveUnassignedMeasurements(group: UnassignedMeasurementGroup, measurementPointID: string): Promise<{ assignment_id: string; resolved_records: number; audit_id: number }> {
  return request("/api/admin/unassigned-measurements/resolve", {
    method: "POST",
    body: JSON.stringify({
      device_id: group.device_id,
      claimed_assignment_id: group.claimed_assignment_id,
      measurement_point_id: measurementPointID,
    }),
  });
}

export async function createGlobalDevice(): Promise<Device> {
  const response = await request<{ device: Device; audit_id: number }>("/api/admin/devices", { method: "POST" });
  return response.device;
}

export function assignGlobalDevice(tenantID: string, measurementPointID: string, deviceID: string): Promise<{ assignment_id: string; starts_at: string; audit_id: number }> {
  return request(`/api/admin/tenants/${tenantID}/measurement-points/${measurementPointID}/assignment`, {
    method: "POST",
    body: JSON.stringify({ device_id: deviceID }),
  });
}

export function unassignGlobalDevice(tenantID: string, measurementPointID: string): Promise<{ device_id: string; audit_id: number }> {
  return request(`/api/admin/tenants/${tenantID}/measurement-points/${measurementPointID}/assignment`, { method: "DELETE" });
}

export function deactivateGlobalDevice(deviceID: string): Promise<void> {
  return request(`/api/admin/devices/${deviceID}/deactivate`, { method: "POST" });
}

export async function createGlobalDeviceEnrollment(deviceID: string): Promise<DeviceEnrollment> {
  const response = await request<{ enrollment: DeviceEnrollment }>(`/api/admin/devices/${deviceID}/enrollment`, {
    method: "POST",
  });
  return response.enrollment;
}

export function requestGlobalDeviceUpdate(deviceID: string): Promise<{ device_id: string; version: string }> {
  return request(`/api/admin/devices/${deviceID}/update`, { method: "POST" });
}

export function restartGlobalDevice(deviceID: string): Promise<{ device_id: string; restart_generation: number; audit_id: number }> {
  return request(`/api/admin/devices/${deviceID}/restart`, { method: "POST" });
}

export function getDeviceNetworkUsage(deviceID: string): Promise<DeviceNetworkUsage> {
  return request(`/api/admin/devices/${deviceID}/agent-settings/network-usage`);
}

export function setDeviceNetworkUsage(deviceID: string, settings: NetworkUsageConfig): Promise<DeviceNetworkUsage> {
  return request(`/api/admin/devices/${deviceID}/agent-settings/network-usage`, {
    method: "PUT",
    body: JSON.stringify(settings),
  });
}

export function resetDeviceNetworkUsage(deviceID: string): Promise<DeviceNetworkUsage> {
  return request(`/api/admin/devices/${deviceID}/agent-settings/network-usage/reset`, { method: "POST" });
}

export function getDeviceHeartbeat(deviceID: string): Promise<DeviceHeartbeat> {
  return request(`/api/admin/devices/${deviceID}/agent-settings/heartbeat`);
}

export function setDeviceHeartbeat(deviceID: string, intervalS: number, offlineAfterS = intervalS * 2): Promise<DeviceHeartbeat> {
  return request(`/api/admin/devices/${deviceID}/agent-settings/heartbeat`, {
    method: "PUT",
    body: JSON.stringify({ interval_s: intervalS, offline_after_s: offlineAfterS }),
  });
}

export function getDeviceOperations(deviceID: string): Promise<DeviceOperations> {
  return request(`/api/admin/devices/${deviceID}/agent-settings/operations`);
}

export function cancelDeviceOperations(deviceID: string): Promise<{ generation: number; cancelled_attempts: number; cancelled_deletions: number; cancelled_agent_update: boolean }> {
  return request(`/api/admin/devices/${deviceID}/agent-settings/operations/cancel`, { method: "POST" });
}

export function getGlobalDeviceExtensions(deviceID: string): Promise<DeviceExtensions> {
  return request<DeviceExtensions>(`/api/admin/devices/${deviceID}/extensions`);
}

export function setGlobalDeviceExtension(deviceID: string, extensionID: string, enabled: boolean): Promise<DeviceExtensions> {
  return request<DeviceExtensions>(`/api/admin/devices/${deviceID}/extensions/${extensionID}`, {
    method: "PUT",
    body: JSON.stringify({ enabled }),
  });
}

export async function getExtensionPublishers(): Promise<ExtensionPublisher[]> {
  const response = await request<{ publishers: ExtensionPublisher[] }>("/api/admin/extension-publishers");
  return response.publishers;
}

export function createExtensionPublisher(name: string, signingPublicKey: string): Promise<{ publisher_id: string; api_token: string }> {
  return request("/api/admin/extension-publishers", {
    method: "POST",
    body: JSON.stringify({ name, signing_public_key: signingPublicKey }),
  });
}

export function deactivateExtensionPublisher(publisherID: string): Promise<{ publisher_id: string; changed: boolean; audit_id: number }> {
  return request(`/api/admin/extension-publishers/${publisherID}/deactivate`, { method: "POST" });
}

export async function getExtensionSubmissions(): Promise<ExtensionSubmission[]> {
  const response = await request<{ submissions: ExtensionSubmission[] }>("/api/admin/extension-submissions");
  return response.submissions;
}

export async function getExtensionDevelopmentAccesses(): Promise<ExtensionDevelopmentAccess[]> {
  const response = await request<{ accesses: ExtensionDevelopmentAccess[] }>("/api/admin/extension-development/accesses");
  return response.accesses;
}

export async function getExtensionDevelopmentDeviceCandidates(): Promise<ExtensionDevelopmentDeviceCandidate[]> {
  const response = await request<{ candidates: ExtensionDevelopmentDeviceCandidate[] }>("/api/admin/extension-development/device-candidates");
  return response.candidates;
}

export async function setExtensionDevelopmentDevice(publisherID: string, deviceID: string): Promise<{ device: ExtensionDevelopmentDevice; credentials?: ExtensionDevelopmentSandboxCredentials }> {
  return request<{ device: ExtensionDevelopmentDevice; credentials?: ExtensionDevelopmentSandboxCredentials }>(`/api/admin/extension-publishers/${publisherID}/development-device`, {
    method: "PUT",
    body: JSON.stringify({ device_id: deviceID }),
  });
}

export function resetExtensionDevelopmentSandboxCredentials(publisherID: string): Promise<{ credentials: ExtensionDevelopmentSandboxCredentials; audit_id: number }> {
  return request(`/api/admin/extension-publishers/${publisherID}/development-sandbox/credentials`, { method: "POST" });
}

export async function getExtensionDevelopmentAttempts(): Promise<ExtensionDevelopmentAttempt[]> {
  const response = await request<{ attempts: ExtensionDevelopmentAttempt[] }>("/api/admin/extension-development/attempts");
  return response.attempts;
}

export function purgeExtensionDevelopmentDevice(deviceID: string): Promise<{ device_id: string; generation: number; audit_id: number; cancelled_attempts: number; completed_deletions: number; deleted_revisions: number; protected_revisions: number }> {
  return request(`/api/admin/extension-development/devices/${deviceID}/purge`, { method: "POST" });
}

export function getExtensionDevelopmentOutput(attemptID: string, after = 0, limit = 500): Promise<ExtensionDevelopmentOutput> {
  return request(`/api/admin/extension-development/attempts/${attemptID}/output?after=${after}&limit=${limit}`);
}

export function cancelExtensionDevelopmentAttempt(attemptID: string): Promise<{ attempt_id: string; state: "cancelling"; audit_id: number; delivery: "published" | "pending" }> {
  return request(`/api/admin/extension-development/attempts/${attemptID}/cancel`, { method: "POST" });
}

export function restartExtensionDevelopmentAttempt(attemptID: string, mode: "restart" | "rebuild" = "restart"): Promise<{ attempt_id: string; revision_id: string; revision_number: number; state: "queued"; audit_id: number; delivery: "published" | "pending" }> {
  return request(`/api/admin/extension-development/attempts/${attemptID}/${mode}`, { method: "POST" });
}

export function deleteExtensionDevelopmentRevision(revisionID: string): Promise<{ revision_id: string; state: string; audit_id: number }> {
  return request(`/api/admin/extension-development/revisions/${revisionID}`, { method: "DELETE" });
}

export function requestExtensionDevelopmentRelease(attemptID: string, version: string, minimumAgentVersion: string, restartPolicy: "never" | "on-failure"): Promise<{ attempt_id: string; state: string; audit_id: number }> {
  return request(`/api/admin/extension-development/attempts/${attemptID}/release`, {
    method: "POST",
    body: JSON.stringify({ version, minimum_agent_version: minimumAgentVersion, restart_policy: restartPolicy }),
  });
}

export function approveExtensionSubmission(submissionID: string, decision: ExtensionReviewDecision): Promise<{ submission_id: string; state: string; release_sequence: number; audit_id: number }> {
  return request(`/api/admin/extension-submissions/${submissionID}/approve`, {
    method: "POST",
    body: JSON.stringify(decision),
  });
}

export function rejectExtensionSubmission(submissionID: string, reason: string): Promise<{ submission_id: string; state: string; audit_id: number }> {
  return request(`/api/admin/extension-submissions/${submissionID}/reject`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export function publishExtensionSubmission(submissionID: string, signature: string): Promise<{ submission_id: string; state: string; audit_id: number }> {
  return request(`/api/admin/extension-submissions/${submissionID}/publish`, {
    method: "POST",
    body: JSON.stringify({ signature }),
  });
}

export function signExtensionSubmission(submissionID: string): Promise<{ submission_id: string; state: string; audit_id: number }> {
  return request(`/api/admin/extension-submissions/${submissionID}/sign`, {
    method: "POST",
  });
}

export function deleteExtensionSubmission(submissionID: string): Promise<{ submission_id: string; state: "deleted"; catalog_removed: boolean; audit_id: number }> {
  return request(`/api/admin/extension-submissions/${submissionID}`, { method: "DELETE" });
}

export function extensionSubmissionArtifactURL(submissionID: string): string {
  return `/api/admin/extension-submissions/${submissionID}/artifact`;
}

export function extensionSubmissionManifestURL(submissionID: string): string {
  return `/api/admin/extension-submissions/${submissionID}/manifest`;
}

export function changeNarrowPassageSegment(groupID: string, segmentID: string, revision: number, purpose: "setup" | "measurement"): Promise<void> {
  return request(`/api/measurement-point-groups/${encodeURIComponent(groupID)}/narrow-passage/segments`, { method: "POST", body: JSON.stringify({ segment_id: segmentID, revision, purpose }) });
}
